import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ExternalLink, Layers, Loader2, X } from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import {
  waysApi, type TechAdoptionAnswer, type TechRecommendation, type TechRecommendationsAnswer, type TechTool,
  type TechnologyStandardAnswer, type TechVerdict,
} from '../../services/waysApi';
import TechEvaluations from './TechEvaluations';

/**
 * Technology (PO-92 EP-10 FE-5, PO92-364): the tools we build on, by sourcing tier, which category
 * each standardises, and the promotions waiting on a super admin. Zane (2026-10-29): open source first,
 * then NVIDIA Inception, then others, "evaluate against alternatives and determin why we chose the
 * one", and a choice becomes the standard only with a super admin's approval.
 * Design review (hs-ways-to-work/ep10#6): the promotion is previewed with who decides it; Promote is
 * shown disabled with its reason for anyone else; a promoted tool reads "pending" until decided.
 *
 * The library and recommendations (PO92-371/372; Zane, 2026-10-29: "the projects need to have the PO
 * number and the project name ... what does it do and what will it do for this app ... a link to the
 * nvidia page ... a justification ... highlight the benefits over what we have"). Design review
 * (hs-ways-to-work/ep10#14-15): the library is its own tab with a contextual link from every
 * recommendation; a tool opens in a right slideout with its own address (?tool=); each shows its
 * sources, evidence and confidence.
 */
const TIER: Record<TechTool['tier'], { label: string; cls: string }> = {
  oss: { label: 'Open source', cls: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' },
  nvidia: { label: 'NVIDIA Inception', cls: 'bg-lime-50 text-lime-800 dark:bg-lime-900/30 dark:text-lime-300' },
  commercial: { label: 'Commercial', cls: 'bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' },
  internal: { label: 'Internal', cls: 'bg-violet-50 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300' },
};
const VERDICT: Record<TechVerdict, { label: string; cls: string }> = {
  Adopt: { label: 'Recommended', cls: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' },
  Defer: { label: 'Not yet', cls: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200' },
  Reject: { label: 'Not recommended', cls: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
};
const RELATION: Record<TechRecommendation['relationship'], string> = {
  support: 'Supports', 'use-as-part': 'Becomes part of', improve: 'Improves', replace: 'Replaces part of',
};
const MAPPER_URL = 'https://github.com/Heuristic-Systems/Nvidia-Benefits-Mapper';
type Tab = 'library' | 'recommendations' | 'evaluations' | 'in-use' | 'standards' | 'promotions';
const TABS: Tab[] = ['library', 'recommendations', 'evaluations', 'in-use', 'standards', 'promotions'];

function TierTag({ tier }: { tier: TechTool['tier'] }) {
  const t = TIER[tier];
  return <span className={clsx('inline-block px-2 py-0.5 rounded-full text-xs whitespace-nowrap', t.cls)}>{t.label}</span>;
}

function VerdictTag({ verdict }: { verdict?: TechVerdict | null }) {
  const v = verdict ? VERDICT[verdict] : null;
  return v ? <span className={clsx('inline-block px-2 py-0.5 rounded-full text-xs whitespace-nowrap', v.cls)}>{v.label}</span> : null;
}

/** "PO-37 APTO", else the project id, else "No PMO project". */
function projectLabel(r: { projectKey?: string | null; projectName?: string | null; project?: string | null }) {
  return [r.projectKey, r.projectName].filter(Boolean).join(' ') || r.project || 'No PMO project';
}

function Links({ links }: { links?: { label: string; url: string }[] | null }) {
  if (!links?.length) return null;
  return (
    <ul className="flex flex-wrap gap-x-3 gap-y-1">
      {links.map((l) => (
        <li key={l.url}>
          <a href={l.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-accent hover:underline">
            {l.label} <ExternalLink className="w-3 h-3" aria-hidden="true" /><span className="sr-only">(opens in a new tab)</span>
          </a>
        </li>
      ))}
    </ul>
  );
}

export default function WaysTechnology({ isSuperAdmin }: { isSuperAdmin: boolean }) {
  const [params, setParams] = useSearchParams();
  const rawTab = params.get('tab');
  const tab: Tab = rawTab === 'catalogue' ? 'library' : rawTab === 'adoption' ? 'in-use'
    : TABS.includes(rawTab as Tab) ? rawTab as Tab : 'library';
  const openTool = params.get('tool');
  const setParam = useCallback((key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: key === 'tab' });
  }, [params, setParams]);
  const setTab = (t: Tab) => setParam('tab', t);
  const showTool = useCallback((id: string | null) => setParam('tool', id), [setParam]);

  const [data, setData] = useState<TechnologyStandardAnswer | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tier, setTier] = useState('');
  const [category, setCategory] = useState('');
  const [q, setQ] = useState('');
  const [promoting, setPromoting] = useState<TechTool | null>(null);
  const [adoption, setAdoption] = useState<TechAdoptionAnswer | null>(null);
  const [adoptProject, setAdoptProject] = useState('');
  const [recs, setRecs] = useState<TechRecommendationsAnswer | null>(null);
  const [recsError, setRecsError] = useState<string | null>(null);

  const load = useCallback(() => {
    waysApi.technologyStandard().then((r) => { setData(r); setError(null); })
      .catch((e) => setError((e as Error).message || 'Ways could not be reached'));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if ((tab === 'in-use' || openTool) && !adoption) waysApi.technologyAdoption().then(setAdoption).catch(() => setAdoption(null));
  }, [tab, openTool, adoption]);
  useEffect(() => {
    if ((tab === 'recommendations' || tab === 'library' || openTool) && !recs && !recsError) {
      waysApi.technologyRecommendations().then(setRecs)
        .catch((e) => setRecsError((e as Error).message || 'Ways could not be reached'));
    }
  }, [tab, openTool, recs, recsError]);

  const std = data?.standard;
  const standardOf = useMemo(() => {
    const m: Record<string, string> = {};
    for (const c of std?.categories || []) if (c.standard) m[c.standard] = c.name;
    return m;
  }, [std]);
  const pendingTools = useMemo(() => new Set((data?.pending || []).map((p) => p.tool)), [data]);
  const tools = useMemo(() => (std?.tools || []).filter((t) => (!tier || t.tier === tier)
    && (!category || t.category === category)
    && (!q || `${t.name} ${t.description || ''}`.toLowerCase().includes(q.toLowerCase()))), [std, tier, category, q]);
  const byCategory = useMemo(() => {
    const m = new Map<string, TechTool[]>();
    for (const t of tools) { if (!m.has(t.category)) m.set(t.category, []); m.get(t.category)!.push(t); }
    return [...m.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [tools]);
  const recCount = useMemo(() => {
    const m: Record<string, number> = {};
    for (const r of recs?.recommendations || []) if (r.verdict === 'Adopt') m[r.tool] = (m[r.tool] || 0) + 1;
    return m;
  }, [recs]);

  if (error) return <p className="p-4 text-sm text-red-600 dark:text-red-400">Technology could not be loaded: {error}</p>;
  if (!data || !std) return <p className="p-4 text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading the technology standard…</p>;

  const field = 'px-2 py-1 text-sm rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100';
  const standards = std.categories.filter((c) => c.standard);
  const tool = openTool ? std.tools.find((t) => t.id === openTool) || null : null;
  const labels: Record<Tab, string> = {
    library: `Library (${std.tools.length})`,
    recommendations: `Recommendations${recs ? ` (${recs.counts.Adopt})` : ''}`,
    evaluations: 'Evaluations',
    'in-use': 'In use',
    standards: `Standards (${standards.length})`,
    promotions: `Promotions (${(data.pending || []).length})`,
  };
  return (
    <div className="flex-1 min-w-0 overflow-y-auto">
      <header className="px-4 pt-4 pb-2 border-b border-slate-200 dark:border-slate-700">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
          <Layers className="w-4 h-4 text-accent" aria-hidden="true" /> Technology
        </h2>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Open source first, then NVIDIA Inception, then commercial; a later tier wins only with a recorded reason.
          Recommendations only: nothing here changes a project. Version {data.version} ({data.source === 'seed' ? 'seed shipped with Ways' : 'from the harness'}).
        </p>
        <div role="tablist" aria-label="Technology views" className="mt-3 flex flex-wrap gap-1 text-sm">
          {TABS.map((id) => (
            <button key={id} type="button" role="tab" aria-selected={tab === id} onClick={() => setTab(id)}
              className={clsx('px-3 py-1.5 border-b-2 -mb-px', tab === id ? 'border-accent text-accent font-medium'
                : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200')}>{labels[id]}</button>
          ))}
        </div>
      </header>

      {tab === 'library' ? (
        <section className="p-4 space-y-4" role="tabpanel" aria-label="Library">
          <div className="flex flex-wrap gap-2 items-center">
            <label className="sr-only" htmlFor="tech-q">Search tools</label>
            <input id="tech-q" className={field} placeholder="Search tools and what they do" value={q} onChange={(e) => setQ(e.target.value)} />
            <label className="sr-only" htmlFor="tech-tier">Tier</label>
            <select id="tech-tier" className={field} value={tier} onChange={(e) => setTier(e.target.value)}>
              <option value="">All tiers</option>
              {std.tiers.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <label className="sr-only" htmlFor="tech-cat">Category</label>
            <select id="tech-cat" className={field} value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="">All categories</option>
              {std.categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
            <span className="text-xs text-slate-500 tabular-nums">{tools.length} shown</span>
          </div>
          {byCategory.length ? byCategory.map(([cat, list]) => (
            <div key={cat}>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">{cat}</h3>
              <ul className="space-y-1">
                {list.map((t) => (
                  <li key={t.id} className="grid grid-cols-[minmax(0,1fr)_auto] sm:grid-cols-[minmax(0,2fr)_auto_minmax(0,1fr)_auto] gap-2 items-center px-3 py-2 rounded-md border border-slate-200 dark:border-slate-700">
                    <div className="min-w-0">
                      <button type="button" onClick={() => showTool(t.id)}
                        className="text-sm text-left text-slate-900 dark:text-slate-100 hover:text-accent hover:underline truncate max-w-full">{t.name}</button>
                      <p className="text-xs text-slate-600 dark:text-slate-300 line-clamp-2">{t.description || 'No description yet.'}</p>
                      <p className="text-xs text-slate-500">{t.licence || 'licence not recorded'}{t.needsReview ? ' · needs review' : ''}
                        {recCount[t.id] ? ` · recommended for ${recCount[t.id]} product${recCount[t.id] === 1 ? '' : 's'}` : ''}</p>
                    </div>
                    <TierTag tier={t.tier} />
                    <div className="text-xs">
                      {standardOf[t.id] ? <span className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">Standard: {standardOf[t.id]}</span>
                        : pendingTools.has(t.id) ? <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200">Pending super-admin approval</span>
                          : <span className="text-slate-400">Candidate</span>}
                    </div>
                    <button type="button" onClick={() => setPromoting(t)}
                      className="text-xs text-accent hover:underline justify-self-end">Propose as standard</button>
                  </li>
                ))}
              </ul>
            </div>
          )) : <p className="text-sm text-slate-500">No tools match these filters.</p>}
        </section>
      ) : tab === 'recommendations' ? (
        <RecommendationsTab data={recs} error={recsError} onTool={showTool} />
      ) : tab === 'evaluations' ? (
        <TechEvaluations std={std} onTool={showTool} />
      ) : tab === 'in-use' ? (
        <InUseTab data={adoption} tools={std.tools} project={adoptProject} onProject={setAdoptProject} onTool={showTool} />
      ) : tab === 'standards' ? (
        <section className="p-4" role="tabpanel" aria-label="Standards">
          {standards.length ? (
            <ul className="space-y-2">
              {standards.map((c) => {
                const t = std.tools.find((x) => x.id === c.standard);
                return (
                  <li key={c.id} className="px-3 py-2 rounded-md border border-slate-200 dark:border-slate-700 text-sm">
                    <div className="flex flex-wrap items-center gap-2"><strong className="text-slate-900 dark:text-slate-100">{c.name}</strong>
                      <span className="text-slate-700 dark:text-slate-200">{t?.name || c.standard}</span>{t ? <TierTag tier={t.tier} /> : null}</div>
                    {c.approved ? <p className="text-xs text-slate-500 mt-1">Approved by a super admin · decision {c.approved.decision}{c.approved.evaluation ? ` · evaluation ${c.approved.evaluation}` : ''}</p> : null}
                    {c.approved?.why ? <p className="text-xs text-slate-600 dark:text-slate-300 mt-1">{c.approved.why}</p> : null}
                  </li>
                );
              })}
            </ul>
          ) : <p className="text-sm text-slate-500">No category has a standard yet. A tool becomes one when a super admin approves its promotion.</p>}
        </section>
      ) : (
        <section className="p-4" role="tabpanel" aria-label="Promotions">
          {(data.pending || []).length ? (
            <ul className="space-y-2">
              {(data.pending || []).map((p) => (
                <li key={p.decisionItem || p.tool} className="px-3 py-2 rounded-md border border-slate-200 dark:border-slate-700 text-sm">
                  <div className="text-slate-900 dark:text-slate-100">Make <strong>{p.toolName}</strong> the standard for <strong>{p.categoryName}</strong></div>
                  <p className="text-xs text-slate-500 mt-1">Pending super-admin approval in PMO Approvals{p.display ? ` (${p.display})` : ''}{p.work ? ` · evaluation ${p.work}` : ''}</p>
                  {p.why ? <p className="text-xs text-slate-600 dark:text-slate-300 mt-1">{p.why}</p> : null}
                </li>
              ))}
            </ul>
          ) : <p className="text-sm text-slate-500">No promotion is waiting.</p>}
        </section>
      )}

      {openTool ? (
        <ToolSlideout id={openTool} tool={tool} standardOf={standardOf[openTool]} pending={pendingTools.has(openTool)}
          recs={recs} recsError={recsError} adoption={adoption} onClose={() => showTool(null)} />
      ) : null}

      {promoting ? (
        <PromoteDialog tool={promoting} categories={std.categories.map((c) => c.name)} isSuperAdmin={isSuperAdmin}
          onClose={() => setPromoting(null)} onDone={() => { setPromoting(null); load(); setTab('promotions'); }} />
      ) : null}
    </div>
  );
}

/** The promotion preview: what will change, who decides it, and why. Anyone may propose; only a super
 * admin decides, in PMO Approvals (the decide step is never here). */
function PromoteDialog({ tool, categories, isSuperAdmin, onClose, onDone }: {
  tool: TechTool; categories: string[]; isSuperAdmin: boolean; onClose: () => void; onDone: () => void;
}) {
  const [category, setCategory] = useState(tool.category);
  const [work, setWork] = useState('');
  const [why, setWhy] = useState('');
  const [busy, setBusy] = useState(false);
  const field = 'w-full px-2 py-1 text-sm rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100';
  const ready = /^(epic|feature|story):[0-9a-f]+$/.test(work.trim()) && why.trim().length >= 20;
  const submit = async () => {
    setBusy(true);
    try {
      await waysApi.proposeStandard({ tool: tool.id, category, work: work.trim(), why: why.trim() });
      toast.success('Proposed: a super admin decides it in PMO Approvals');
      onDone();
    } catch (e) { toast.error((e as Error).message || 'Could not propose it', { duration: 8000 }); }
    finally { setBusy(false); }
  };
  return (
    <div role="dialog" aria-modal="true" aria-labelledby="promote-title" className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-lg bg-white dark:bg-slate-800 shadow-xl p-4 space-y-3" onClick={(e) => e.stopPropagation()}>
        <h3 id="promote-title" className="text-sm font-semibold text-slate-900 dark:text-slate-100">Propose {tool.name} as a standard</h3>
        <p className="text-xs text-slate-600 dark:text-slate-300">
          This files one approval request in PMO Approvals. Only a super admin can approve it; then {tool.name} becomes the
          standard for the category and the governor flags other tools there. {isSuperAdmin
            ? 'You are a super admin: you will decide it in Approvals, not here.'
            : 'You can propose it; a super admin decides it.'}
        </p>
        <label className="block text-xs text-slate-600 dark:text-slate-300" htmlFor="pr-cat">Category</label>
        <select id="pr-cat" className={field} value={category} onChange={(e) => setCategory(e.target.value)}>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <label className="block text-xs text-slate-600 dark:text-slate-300" htmlFor="pr-work">Evaluation: the epic, feature or story it belongs to (id)</label>
        <input id="pr-work" className={field} placeholder="feature:…" value={work} onChange={(e) => setWork(e.target.value)} />
        <label className="block text-xs text-slate-600 dark:text-slate-300" htmlFor="pr-why">Why this one: the open-source and Inception alternatives, and why they lost</label>
        <textarea id="pr-why" rows={4} className={field} value={why} onChange={(e) => setWhy(e.target.value)} />
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-1 text-xs rounded-md border border-slate-300 dark:border-slate-600">Cancel</button>
          <button type="button" onClick={submit} disabled={!ready || busy}
            title={ready ? undefined : 'Name the evaluation item and give at least a sentence of reason'}
            className="px-3 py-1 text-xs rounded-md bg-accent text-white disabled:opacity-50">File for approval</button>
        </div>
      </div>
    </div>
  );
}

/** One tool: what it does, its pages, where it came from and how sure that is, where we use it, and
 * what it is recommended for. A right slideout with its own address (?tool=<id>); Escape closes it. */
function ToolSlideout({ id, tool, standardOf, pending, recs, recsError, adoption, onClose }: {
  id: string; tool: TechTool | null; standardOf?: string; pending: boolean; recs: TechRecommendationsAnswer | null;
  recsError: string | null; adoption: TechAdoptionAnswer | null; onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const mine = (recs?.recommendations || []).filter((r) => r.tool === id);
  const used = (adoption?.records || []).filter((r) => r.tool === id);
  const prov = tool?.provenance;
  const h = 'text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1';
  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <div className="absolute inset-0 bg-black/20" onClick={onClose} aria-hidden="true" />
      <aside role="dialog" aria-modal="true" aria-labelledby="tool-title"
        className="relative w-full max-w-[560px] h-full bg-white dark:bg-slate-800 shadow-xl border-l border-slate-200 dark:border-slate-700 flex flex-col">
        <header className="shrink-0 p-4 border-b border-slate-200 dark:border-slate-700 flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 id="tool-title" className="text-base font-semibold text-slate-900 dark:text-slate-100 break-words">{tool?.name || id}</h3>
            {tool ? (
              <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                <TierTag tier={tool.tier} />
                <span>{tool.category}</span>
                {tool.vendor ? <span>· {tool.vendor}</span> : null}
                {tool.type ? <span>· {tool.type}</span> : null}
                <span>· {tool.licence || 'licence not recorded'}</span>
              </div>
            ) : null}
          </div>
          <button type="button" onClick={onClose} aria-label="Close" autoFocus
            className="p-1.5 rounded text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 dark:text-slate-400"><X className="w-4 h-4" /></button>
        </header>
        <div className="flex-1 overflow-y-auto p-4 space-y-5 text-sm">
          {!tool ? <p className="text-slate-500">This tool is not in the library.</p> : (
            <>
              <section>
                <h4 className={h}>What it does</h4>
                <p className="text-slate-800 dark:text-slate-200">{tool.description || 'No description yet.'}</p>
                <div className="mt-2"><Links links={tool.links} /></div>
              </section>
              <section>
                <h4 className={h}>Status</h4>
                <p className="text-slate-700 dark:text-slate-300">
                  {standardOf ? `The standard for ${standardOf}, approved by a super admin.`
                    : pending ? 'Proposed as a standard; waiting on a super admin in PMO Approvals.'
                      : 'A candidate: not the standard for any category.'}
                </p>
              </section>
              <section>
                <h4 className={h}>Where this comes from</h4>
                <p className="text-xs text-slate-600 dark:text-slate-300">
                  {prov?.source === 'nvidia-benefits-mapper'
                    ? <>From the <a href={MAPPER_URL} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">Nvidia-Benefits-Mapper</a></>
                    : prov?.source ? <>From {prov.source}</> : 'Source not recorded'}
                  {prov?.sourceType ? ` (${prov.sourceType.replace(/_/g, ' ')})` : ''}
                  {typeof prov?.confidence === 'number' ? ` · confidence ${Math.round(prov.confidence * 100)}%` : ''}
                  {tool.needsReview ? ' · the match needs a person to review it' : ''}
                </p>
                {prov?.ref?.startsWith('http') ? <a href={prov.ref} target="_blank" rel="noopener noreferrer" className="text-xs text-accent hover:underline break-all">{prov.ref}</a> : null}
              </section>
            </>
          )}
          <section>
            <h4 className={h}>Recommended for</h4>
            {recsError ? <p className="text-xs text-red-600 dark:text-red-400">Recommendations could not be loaded: {recsError}</p>
              : !recs ? <p className="text-xs text-slate-500">Loading…</p>
                : mine.length ? <ul className="space-y-2">{mine.map((r) => <RecommendationCard key={r.id} r={r} compact />)}</ul>
                  : <p className="text-xs text-slate-500">No product has a recommendation for this tool.</p>}
          </section>
          <section>
            <h4 className={h}>Found in our repositories</h4>
            {!adoption ? <p className="text-xs text-slate-500">Loading…</p> : used.length ? (
              <ul className="space-y-1">
                {used.map((r) => (
                  <li key={r.repository} className="text-xs text-slate-700 dark:text-slate-300">
                    <span className="font-medium">{projectLabel(r)}</span> · {r.repository.split('/').pop()}
                    {r.evidence?.[0] ? <span className="font-mono text-slate-500"> · {r.evidence[0].path}{r.evidence[0].line ? ` (${r.evidence[0].line})` : ''}</span> : null}
                  </li>
                ))}
              </ul>
            ) : <p className="text-xs text-slate-500">The repository scan did not find it.</p>}
          </section>
        </div>
      </aside>
    </div>
  );
}

/** One recommendation: the project (PO number and name), what the tool will do for that product, what
 * the product uses today and what the tool does better, the benefits and the costs, and the evidence. */
function RecommendationCard({ r, onTool, compact }: { r: TechRecommendation; onTool?: (id: string) => void; compact?: boolean }) {
  const [open, setOpen] = useState(false);
  const j = r.justification;
  const s = r.scores || {};
  return (
    <li className="px-3 py-2 rounded-md border border-slate-200 dark:border-slate-700">
      <div className="flex flex-wrap items-center gap-2">
        <VerdictTag verdict={r.verdict} />
        {compact ? null : onTool ? (
          <button type="button" onClick={() => onTool(r.tool)} className="text-sm font-medium text-left text-slate-900 dark:text-slate-100 hover:text-accent hover:underline">
            {r.toolDetails.name || r.tool}
          </button>
        ) : <span className="text-sm font-medium text-slate-900 dark:text-slate-100">{r.toolDetails.name || r.tool}</span>}
        <span className="text-xs text-slate-500 dark:text-slate-400">{RELATION[r.relationship]} <span className="text-slate-700 dark:text-slate-200">{r.product}</span>
          {' · '}<span className="text-slate-700 dark:text-slate-200">{projectLabel(r)}</span></span>
        {r.priority ? <span className="text-xs text-slate-500 tabular-nums">{r.priority}{r.wsjf != null ? ` · WSJF ${r.wsjf.toFixed(1)}` : ''}</span> : null}
      </div>
      {!compact && r.toolDetails.description ? <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{r.toolDetails.description}</p> : null}
      <dl className="mt-1.5 space-y-1 text-xs">
        <div><dt className="inline font-medium text-slate-700 dark:text-slate-200">What it will do for {r.product}: </dt>
          <dd className="inline text-slate-700 dark:text-slate-300">{r.how || 'not recorded'}</dd></div>
        {r.today ? <div><dt className="inline font-medium text-slate-700 dark:text-slate-200">Today: </dt>
          <dd className="inline text-slate-700 dark:text-slate-300">{r.today}</dd></div> : null}
        {j.overCurrent ? (
          <div className="rounded bg-slate-50 dark:bg-slate-900/40 px-2 py-1">
            <dt className="font-medium text-slate-700 dark:text-slate-200">Better than {j.overCurrent.current || 'what we have'} because</dt>
            <dd className="text-slate-700 dark:text-slate-300">{j.overCurrent.benefit || 'the benefit over the current tool is not recorded yet'}</dd>
          </div>
        ) : null}
        <div><dt className="inline font-medium text-slate-700 dark:text-slate-200">Why: </dt>
          <dd className="inline text-slate-700 dark:text-slate-300">{j.reason}</dd></div>
      </dl>
      <button type="button" onClick={() => setOpen((x) => !x)} aria-expanded={open} className="mt-1 text-xs text-accent hover:underline">
        {open ? 'Hide the benefits, costs and evidence' : `Benefits (${j.benefits.length}), costs (${j.costs.length}) and evidence`}
      </button>
      {open ? (
        <div className="mt-1.5 grid gap-2 sm:grid-cols-2 text-xs">
          <div>
            <h5 className="font-medium text-slate-700 dark:text-slate-200">Benefits</h5>
            {j.benefits.length ? <ul className="list-disc pl-4 text-slate-700 dark:text-slate-300">{j.benefits.map((b) => <li key={b}>{b}</li>)}</ul>
              : <p className="text-slate-500">None recorded.</p>}
          </div>
          <div>
            <h5 className="font-medium text-slate-700 dark:text-slate-200">Costs and conditions</h5>
            {j.costs.length ? <ul className="list-disc pl-4 text-slate-700 dark:text-slate-300">{j.costs.map((c) => <li key={c}>{c}</li>)}</ul>
              : <p className="text-slate-500">None recorded.</p>}
          </div>
          <div className="sm:col-span-2 text-slate-600 dark:text-slate-300">
            <h5 className="font-medium text-slate-700 dark:text-slate-200">Evidence</h5>
            <p className="tabular-nums">Evidence {s.evidence_strength ?? '–'}/5 · confidence {s.confidence ?? '–'}/5 · value {s.value ?? '–'}/5 ·
              readiness {s.readiness ?? '–'}/5 · effort {r.effort || '–'} · horizon {r.horizon || '–'}</p>
            <p>From the <a href={MAPPER_URL} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">Nvidia-Benefits-Mapper</a> ({r.id}).</p>
            <div className="mt-1"><Links links={r.toolDetails.links} /></div>
          </div>
        </div>
      ) : null}
    </li>
  );
}

/** Recommendations by project: each names its PO number and project, and justifies itself. Advisory
 * (Zane, 2026-10-29: "for now we are just making recommendations"). */
function RecommendationsTab({ data, error, onTool }: {
  data: TechRecommendationsAnswer | null; error: string | null; onTool: (id: string) => void;
}) {
  const [project, setProject] = useState<string | null>(null);
  const [verdict, setVerdict] = useState<TechVerdict | ''>('Adopt');
  const [q, setQ] = useState('');
  const projects = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of data?.recommendations || []) m.set(r.project || '', projectLabel(r));
    return [...m.entries()].sort(([, a], [, b]) => a.localeCompare(b, undefined, { numeric: true }));
  }, [data]);
  if (error) return <p className="p-4 text-sm text-red-600 dark:text-red-400">Recommendations could not be loaded: {error}</p>;
  if (!data) return <p className="p-4 text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading the recommendations…</p>;
  const ql = q.toLowerCase();
  const list = data.recommendations.filter((r) => (project === null || (r.project || '') === project)
    && (!verdict || r.verdict === verdict)
    && (!ql || `${r.toolDetails.name} ${r.product} ${r.how || ''} ${r.today || ''}`.toLowerCase().includes(ql)));
  const field = 'px-2 py-1 text-sm rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100';
  return (
    <section className="p-4 space-y-3" role="tabpanel" aria-label="Recommendations">
      <p className="text-xs text-slate-600 dark:text-slate-300">
        What each tool would do for each product, what the product uses today, and why. From
        the <a href={MAPPER_URL} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">Nvidia-Benefits-Mapper</a>
        {data.asOf ? ` as of ${data.asOf}` : ''}. Recommendations only: a project decides, and a standard needs a super admin&apos;s approval.
      </p>
      <div className="flex flex-wrap gap-2 items-center">
        <label className="sr-only" htmlFor="rec-proj">Project</label>
        <select id="rec-proj" className={field} value={project === null ? '*' : project} onChange={(e) => setProject(e.target.value === '*' ? null : e.target.value)}>
          <option value="*">All projects ({projects.length})</option>
          {projects.map(([id, label]) => <option key={id || 'none'} value={id}>{label}</option>)}
        </select>
        <label className="sr-only" htmlFor="rec-verdict">Verdict</label>
        <select id="rec-verdict" className={field} value={verdict} onChange={(e) => setVerdict(e.target.value as TechVerdict | '')}>
          <option value="Adopt">Recommended ({data.counts.Adopt})</option>
          <option value="Defer">Not yet ({data.counts.Defer})</option>
          <option value="Reject">Not recommended ({data.counts.Reject})</option>
          <option value="">All verdicts</option>
        </select>
        <label className="sr-only" htmlFor="rec-q">Search</label>
        <input id="rec-q" className={field} placeholder="Search tool, product or use" value={q} onChange={(e) => setQ(e.target.value)} />
        <span className="text-xs text-slate-500 tabular-nums">{list.length} shown</span>
      </div>
      {list.length ? (
        <ul className="space-y-2">{list.slice(0, 200).map((r) => <RecommendationCard key={r.id} r={r} onTool={onTool} />)}</ul>
      ) : <p className="text-sm text-slate-500">No recommendation matches these filters.</p>}
      {list.length > 200 ? <p className="text-xs text-slate-500">Showing the first 200 of {list.length}; narrow by project or search.</p> : null}
    </section>
  );
}

/** What the repository scan found, per project, each with its evidence. Nobody is asked to confirm yet
 * (Zane, 2026-10-29: "for now we are just making recommendations"). */
function InUseTab({ data, tools, project, onProject, onTool }: {
  data: TechAdoptionAnswer | null; tools: TechTool[]; project: string; onProject: (p: string) => void; onTool: (id: string) => void;
}) {
  if (!data) return <p className="p-4 text-sm text-slate-500">Loading what the scan found…</p>;
  const byId = Object.fromEntries(tools.map((t) => [t.id, t]));
  const key = (r: TechAdoptionAnswer['records'][number]) => r.project || r.repository;
  const projects = [...new Map(data.records.map((r) => [key(r), r.project ? projectLabel(r) : r.repository])).entries()]
    .sort(([, a], [, b]) => a.localeCompare(b, undefined, { numeric: true }));
  const recs = data.records.filter((r) => !project || key(r) === project);
  const field = 'px-2 py-1 text-sm rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100';
  const name = (id: string) => (
    <button type="button" onClick={() => onTool(id)} className="text-left text-slate-900 dark:text-slate-100 hover:text-accent hover:underline">{byId[id]?.name || id}</button>
  );
  return (
    <section className="p-4 space-y-3" role="tabpanel" aria-label="In use">
      <p className="text-xs text-slate-600 dark:text-slate-300">
        Found by scanning every repository{data.scannedAt ? ` on ${new Date(data.scannedAt * 1000).toLocaleDateString()}` : ''}:
        dependency files, Dockerfiles and Kubernetes images. Found, not yet confirmed by the project.
      </p>
      <div className="flex flex-wrap gap-2 items-center">
        <label className="sr-only" htmlFor="ad-proj">Project</label>
        <select id="ad-proj" className={field} value={project} onChange={(e) => onProject(e.target.value)}>
          <option value="">All projects ({projects.length})</option>
          {projects.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
        </select>
        <span className="text-xs text-slate-500 tabular-nums">{recs.length} findings</span>
      </div>
      {!project ? (
        <ul className="space-y-1">
          {data.byTool.map((t) => (
            <li key={t.tool} className="flex flex-wrap items-center gap-2 px-3 py-2 rounded-md border border-slate-200 dark:border-slate-700 text-sm">
              <span className="font-medium min-w-0">{name(t.tool)}</span>
              {byId[t.tool] ? <TierTag tier={byId[t.tool].tier} /> : null}
              <span className="text-xs text-slate-500 tabular-nums">{t.repositories} repos · {t.projects.length} projects</span>
            </li>
          ))}
        </ul>
      ) : (
        <div className="overflow-x-auto rounded-md border border-slate-200 dark:border-slate-700">
          <table className="min-w-[560px] w-full text-sm">
            <thead><tr className="text-left text-xs text-slate-500"><th className="px-3 py-2">Tool</th><th className="px-3 py-2">Tier</th><th className="px-3 py-2">Repository</th><th className="px-3 py-2">Evidence</th></tr></thead>
            <tbody>
              {recs.map((r) => (
                <tr key={`${r.repository}-${r.tool}`} className="border-t border-slate-200 dark:border-slate-700">
                  <td className="px-3 py-2">{name(r.tool)}</td>
                  <td className="px-3 py-2">{byId[r.tool] ? <TierTag tier={byId[r.tool].tier} /> : null}</td>
                  <td className="px-3 py-2 text-slate-600 dark:text-slate-300">{r.repository.split('/').pop()}</td>
                  <td className="px-3 py-2 font-mono text-xs text-slate-500 break-all">{(r.evidence || [])[0]?.path} {(r.evidence || [])[0]?.line}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
